import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@progress/kendo-react-buttons'
import { Label, Error } from '@progress/kendo-react-labels'
import {
  Card,
  CardActions,
  CardBody,
  CardHeader,
  CardTitle,
  GridLayout,
  GridLayoutItem,
  StackLayout
} from '@progress/kendo-react-layout'

import {
  FiscalMonthDropDownList,
  BranchSelect,
  ExpenseSelect,
  DepartmentDropDownList,
  ConfirmDialog,
  Message,
  ErrorMessage,
  ErrorMessages
} from '../../../components/parts'
import { useAccessibleAuthority, usePageTitle } from '../../../components/hooks'
import { SimulationExportGateway } from '../../../gateway/simulation/SimulationExportGateway'
import { GetSimulationExportReqParams } from '@gateway.Simulation'
import { useAppSelector, CommonActions, useAppDispatch } from '../../../stores'
import { BlobFileService, MessageProvider } from '../../../services'
import { Authority } from '../../../gateway/system/Authority'

export const SimulationExport: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch)
  usePageTitle('山積シミュレーション出力')
  const userProp = useAppSelector((state) => state.common.userProp)
  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)

  /** システム年 */
  const systemYear = new Date().getFullYear()
  /** システム月 */
  const systemMonth = new Date().getMonth() + 1
  const [confirmDialogShow, setConfirmDialogShow] = useState(false)
  const [messageId, setMessageId] = useState('')
  const [messageArgslist, setMessageArgslist] = useState([''])
  const [department, setDepartment] = useState('1')
  const [expenseCdList, setExpenseCdList] = useState<string[]>([])
  const [branchCdList, setBranchCdList] = useState<string[]>([])
  const [year, setYear] = useState(systemYear.toString())
  const [month, setMonth] = useState(systemMonth.toString())
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [errorMessageId, setErrorMessageId] = useState('')
  const [errorMessageArgs, setErrorMessageArgs] = useState([''])
  const [timeErrorVisible, setTimeErrorVisible] = useState(false)
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  useEffect(() => {
    if (userProp?.authority === 3) {
      setDepartment('2')
    }
  }, [userProp?.authority])

  /** フォーマット */
  const handleDepartment = (val: string) => {
    setDepartment(val)
  }
  /** 費目 */
  const handleExpenses = (valueList: string[]) => {
    setExpenseCdList(valueList)
  }
  /** 支店 */
  const handleBranch = (valueList: string[]) => {
    setBranchCdList(valueList)
  }
  /** 年 */
  const changeYear = (val: number) => {
    setYear(val.toString())
  }
  /** 月 */
  const changeMonth = (val: number) => {
    setMonth(val.toString())
  }

  /** 出力 */
  const handleOutPut = () => {
    setTimeErrorVisible(false)
    setMessageId('I002')
    setMessageArgslist(['山積シミュレーション', '出力開始'])
    setConfirmDialogShow(true)
  }

  /** ConfirmDialog イベントの確認 */
  const dialogComfirmHanaleBtn = () => {
    if (messageId === 'I002') {
      setConfirmDialogShow(false)
      // 必須チェック
      if (department === '' || year === '' || month === '' || branchCdList.length === 0 || expenseCdList.length === 0) {
        const checkMessage: string[] = []

        if (branchCdList.length === 0) {
          checkMessage.push('支店')
        }
        if (expenseCdList.length === 0) {
          checkMessage.push('費目')
        }
        if (department === '') {
          checkMessage.push('フォーマット')
        }
        if (year === '') {
          checkMessage.push('山積実施年度')
        }
        if (month === '') {
          checkMessage.push('山積実施月度')
        }
        setErrorMessageId('E001')
        setErrorMessageArgs([checkMessage.join(','), '出力'])
        setTimeErrorVisible(true)
        return
      }
      // 山積実施年月度チェック
      if (
        Number(year) > systemYear ||
        (Number(year) === systemYear && Number(month) > systemMonth)
      ) {
        setMonth(systemMonth.toString())
        setYear(systemYear.toString())
        setErrorMessageId('E014')
        setErrorMessageArgs(['山積実施年月度', '当月以前'])
        setTimeErrorVisible(true)
        return
      }

      dispatch(CommonActions.setIsLoading(true))
      const reqParams: GetSimulationExportReqParams = {
        expense: expenseCdList,
        branchCd: branchCdList,
        targetYear: year,
        targetMonth: Number(month) > 9 ? month : `0${month}`,
        format: department,
        displayStartYear
      }
      SimulationExportGateway.getSimulationExport(reqParams)
        .then((res) => {
          if (!res.error) {
            if (typeof res.data.fileUrl === 'string') {
              setNotificationMessages([
                {
                  id: 'I004',
                  content: MessageProvider.provide('I004', '山積シミュレーション出力')
                }
              ])
              BlobFileService.downloadRedirectTo(res.data.fileUrl)
            }
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const apiMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(apiMessages)
            }
          }
        })
        .finally(() => {
          dispatch(CommonActions.setIsLoading(false))
        })
    }
  }

  return (
    <>
      <Card style={{ width: '40%', margin: '30px auto' }}>
        <CardHeader style={{ textAlign: 'center' }}>
          <CardTitle>山積シミュレーション出力</CardTitle>
        </CardHeader>
        <CardBody>
          <GridLayout cols={[{ width: '20%' }]} gap={{ cols: 5, rows: 5 }}>
            <GridLayoutItem row={1} col={1}>
              <Label>フォーマット</Label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={2}>
              <DepartmentDropDownList
                onChange={handleDepartment}
                value={department}
              />
            </GridLayoutItem>
            <GridLayoutItem row={2} col={1}>
              <Label>費目</Label>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={2}>
              <ExpenseSelect
                multiple
                onChange={handleExpenses}
                value={expenseCdList}
                autoClose={false}
              />
            </GridLayoutItem>
            <GridLayoutItem row={3} col={1}>
              <Label>支店</Label>
            </GridLayoutItem>
            <GridLayoutItem row={3} col={2}>
              <BranchSelect
                multiple
                onChange={handleBranch}
                value={branchCdList}
                autoClose={false}
              />
            </GridLayoutItem>
            <GridLayoutItem row={4} col={1}>
              <Label>山積実施年月度</Label>
            </GridLayoutItem>
            <GridLayoutItem row={4} col={2}>
              <FiscalMonthDropDownList
                startYear={2018}
                fyddlValue={Number(year)}
                fmddlValue={Number(month)}
                onYearChange={changeYear}
                onMonthChange={changeMonth}
              />
              {timeErrorVisible && (
                <Error>
                  <Message id={errorMessageId} args={errorMessageArgs} />
                </Error>
              )}
            </GridLayoutItem>
          </GridLayout>
        </CardBody>
        <CardActions layout="end">
          <StackLayout gap={5}>
            <Button
              style={{ minWidth: 120 }}
              onClick={() => {
                navigate(-1)
              }}
            >
              戻る
            </Button>
            <Button
              style={{ minWidth: 120 }}
              themeColor="primary"
              onClick={handleOutPut}
            >
              出力
            </Button>
          </StackLayout>
        </CardActions>
      </Card>
      <ConfirmDialog
        open={confirmDialogShow}
        id={messageId}
        renderCloseButton
        title={'確認'}
        args={messageArgslist}
        onClickOk={dialogComfirmHanaleBtn}
        onClickCancel={() => setConfirmDialogShow(false)}
      />
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
}
